import React, { useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { Button } from 'primereact/button';
import { formatDate } from '../../../../../../utils/date-format';
import {
  InstallmentWithPlanItemsDetails,
  PaymentInstallmentStatus,
} from '../../../../../../api/types/payments/installments/entities';
import { deleteInstallmentPlanOperation } from '../operations/delete-installment-plan-operation';
import { UserSummaryType } from '../../../../../../api/types/user/user-summary.type';
import { WarningDialog } from '../../../../../../component/ui/warning-dialog/warning-dialog';

interface InstallmentsTableProps {
  user: UserSummaryType;
  plan: InstallmentWithPlanItemsDetails;
  onDeleted?: () => void;
}

const getStatusSeverity = (
  status: PaymentInstallmentStatus,
): 'success' | 'warning' | 'danger' | 'info' => {
  switch (status) {
    case PaymentInstallmentStatus.PAID:
      return 'success';
    case PaymentInstallmentStatus.PENDING:
      return 'warning';
    case PaymentInstallmentStatus.FAILED:
    case PaymentInstallmentStatus.CANCELLED:
      return 'danger';
    default:
      return 'warning';
  }
};

const getStatusLabel = (status: PaymentInstallmentStatus): string => {
  switch (status) {
    case PaymentInstallmentStatus.PENDING:
      return 'ממתין לתשלום';
    case PaymentInstallmentStatus.PAID:
      return 'שולם';
    case PaymentInstallmentStatus.FAILED:
      return 'נכשל';
    case PaymentInstallmentStatus.CANCELLED:
      return 'בוטל';
    default:
      return 'לא ידוע';
  }
};

export const InstallmentsTable: React.FC<InstallmentsTableProps> = ({
  user,
  plan,
  onDeleted,
}) => {
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showDeleteWarning, setShowDeleteWarning] = useState(false);

  const handleDeleteClick = () => {
    setShowDeleteWarning(true);
  };

  const handleDeleteConfirm = async () => {
    await deleteInstallmentPlanOperation(
      user,
      plan.installmentPlan.id,
      setDeleteLoading,
      () => {
        setShowDeleteWarning(false);
        onDeleted?.();
      },
    );
  };

  return (
    <div>
      <WarningDialog
        header="מחיקת הוראת קבע"
        isVisible={showDeleteWarning}
        onHide={() => setShowDeleteWarning(false)}
        addIcon
        content="האם אתה בטוח שברצונך למחוק את הוראת הקבע?"
        dangerousContent="פעולה זו תבטל את כל התשלומים הממתינים ואינה ניתנת לביטול."
        onConfirm={handleDeleteConfirm}
        confirmLabel="מחק"
        confirmLoading={deleteLoading}
        cancelLabel="ביטול"
      />
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginBottom: '12px',
        }}
      >
        <Button
          icon="pi pi-trash"
          label="מחק הוראת קבע"
          severity="danger"
          size="small"
          onClick={handleDeleteClick}
        />
      </div>
      <DataTable
        value={plan.installment}
        rowHover
        emptyMessage={
          <div style={{ textAlign: 'right' }}>לא נמצאו תשלומים</div>
        }
        dataKey="id"
        scrollable
        scrollHeight="300px"
        tableStyle={{
          direction: 'rtl',
        }}
      >
        <Column
          field="installmentNumber"
          header="תשלום מספר"
          style={{ textAlign: 'right' }}
          headerStyle={{ textAlign: 'right' }}
          body={(rowData) =>
            `${rowData.installmentNumber} / ${plan.installmentPlan.numberOfInstallments}`
          }
        />
        <Column
          field="amount"
          header="סכום תשלום"
          style={{ textAlign: 'right' }}
          headerStyle={{ textAlign: 'right' }}
          body={(rowData) => `₪${rowData.amount?.toLocaleString()}`}
        />
        <Column
          field="dueDate"
          header="תאריך לתשלום"
          style={{ textAlign: 'right' }}
          headerStyle={{ textAlign: 'right' }}
          body={(rowData) => formatDate(rowData.dueDate)}
        />
        <Column
          field="paidAt"
          header="תאריך תשלום בפועל"
          style={{ textAlign: 'right' }}
          headerStyle={{ textAlign: 'right' }}
          body={(rowData) =>
            rowData.paidAt ? formatDate(rowData.paidAt) : '-'
          }
        />
        <Column
          field="status"
          header="סטטוס"
          style={{ textAlign: 'right' }}
          headerStyle={{ textAlign: 'right' }}
          body={(rowData) => (
            <Tag
              style={{ minWidth: '100px' }}
              value={getStatusLabel(rowData.status)}
              severity={getStatusSeverity(rowData.status)}
            />
          )}
        />
      </DataTable>
    </div>
  );
};
